// src/config/ifConfig.js
const trimSlash = (s) => String(s || "").trim().replace(/\/+$/, "");

const safeAuthMode = (raw) => {
  const v = String(raw || "").trim().toLowerCase();
  // ✅ strict allowlist
  if (v === "cookie" || v === "amplify") return v;
  return "cookie";
};

const parseBool = (v, fallback = false) => {
  if (typeof v === "boolean") return v;
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
};

const isSameOrigin = (apiUrl, frontendOrigin) => {
  try {
    const u = new URL(String(apiUrl), frontendOrigin);
    return u.origin === frontendOrigin;
  } catch {
    return false;
  }
};

export function getIfConfig() {
  // runtime config first (preferred)
  const runtime = (typeof window !== "undefined" && window.__IF_CONFIG__) || {};

  // env fallback (build-time)
  const envApi = process.env.REACT_APP_API_URL;
  const envFrontend = process.env.REACT_APP_FRONTEND_URL;
  const envMode = process.env.REACT_APP_AUTH_MODE;

  // ✅ new (optional): explicitly allow credentials on cross-site API
  const envWithCreds = process.env.REACT_APP_WITH_CREDENTIALS;

  const API_URL = trimSlash(runtime.API_URL || envApi || "");
  const FRONTEND_URL = trimSlash(
    runtime.FRONTEND_URL ||
      envFrontend ||
      (typeof window !== "undefined" ? window.location.origin : "")
  );

  const AUTH_MODE = safeAuthMode(runtime.AUTH_MODE || envMode || "cookie");

  if (!API_URL) {
    throw new Error("API URL is not configured. Set public/if-config.js or REACT_APP_API_URL.");
  }

  const frontendOrigin = FRONTEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const IS_SAME_ORIGIN_API = isSameOrigin(API_URL, frontendOrigin);

  /**
   * ✅ Cookie mode rules:
   * - If API is same-origin -> credentials are safe by default.
   * - If API is cross-origin -> require explicit opt-in (WITH_CREDENTIALS=true).
   */
  const WITH_CREDENTIALS =
    AUTH_MODE === "cookie"
      ? (IS_SAME_ORIGIN_API ? true : parseBool(runtime.WITH_CREDENTIALS ?? envWithCreds, false))
      : false;

  // ✅ Helpful guard: prevent accidental broken cookie auth in prod
  if (AUTH_MODE === "cookie" && !IS_SAME_ORIGIN_API && !WITH_CREDENTIALS) {
    // Don’t throw (could be intentional), but warn loudly in dev.
    if (typeof window !== "undefined" && String(process?.env?.NODE_ENV || "") !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "[IF_CONFIG] AUTH_MODE=cookie but API_URL is cross-origin and WITH_CREDENTIALS is not enabled. " +
          "If your auth relies on cookies/CSRF, set WITH_CREDENTIALS=true in runtime config or REACT_APP_WITH_CREDENTIALS."
      );
    }
  }

  return {
    API_URL,
    FRONTEND_URL,
    AUTH_MODE,
    WITH_CREDENTIALS,
    IS_SAME_ORIGIN_API,
  };
}